import { App } from "vue";
import pinia from "@/plugins/PiniaPlugin";

import {
    cptProductKey,
    dmiProductKey,
    dmtProductKey,
    mttProductKey,
    osProductKey,
    type ProductDefinition,
    psProductKey,
    sfProductKey,
    spProductKey,
    sssProductKey,
    tamProductKey,
    tspProductKey
} from "@/types/products/product-definitions";

import { useCheckPointTrader } from "@/composables/products/check-point-trader";
import { useDarkMoneyIndicator } from "@/composables/products/dark-money-indicator";
import { useDeepMarketTrader } from "@/composables/products/deep-market-trader";
import { useMonthlyTrendTrader } from "@/composables/products/monthly-trend-trader";
import { useOptionsSoup } from "@/composables/products/options-soup";
import { useProfitSkimmer } from "@/composables/products/profit-skimmer";
import { useSectorFocus } from "@/composables/products/sector-focus";
import { useSectorProphets } from "@/composables/products/sector-prophets";
import { useShadowSpikeScanner } from "@/composables/products/shadow-spike-scanner";
import { useTechnicalAnalysisMillionaire } from "@/composables/products/technical-analysis-millionaire";
import { useTrueSovereignProfits } from "@/composables/products/true-sovereign-profits";
import { useProductsStore } from "@/stores/Products";

// Registry of every per-product context factory keyed by productKey/slug.
// Lets non-component callers (e.g. SidebarMenu) reach into a product's
// actionsMap to sum its per-tab unread counts without having to call
// `inject()` at setup time per product. Each factory is the same one the
// plugin also wires up via `app.provide()` below, so consumers stay in sync.
export const productFactories: Record< string, () => ProductDefinition > = {
    [ cptProductKey ]: useCheckPointTrader,
    [ dmiProductKey ]: useDarkMoneyIndicator,
    [ dmtProductKey ]: useDeepMarketTrader,
    [ mttProductKey ]: useMonthlyTrendTrader,
    [ osProductKey ]: useOptionsSoup,
    [ psProductKey ]: useProfitSkimmer,
    [ sfProductKey ]: useSectorFocus,
    [ spProductKey ]: useSectorProphets,
    [ sssProductKey ]: useShadowSpikeScanner,
    [ tamProductKey ]: useTechnicalAnalysisMillionaire,
    [ tspProductKey ]: useTrueSovereignProfits,
};

const ProductContextsPlugin = {
    install( app: App< Element > ) {
        // WHY: Register providers SYNCHRONOUSLY at install time. Previously these
        // were registered inside `productsStore.initializeAsync().then(...)`,
        // which meant components mounting on the initial route (e.g.
        // ProductWorkspace) could call `inject(productSlug)` BEFORE the providers
        // were registered. On miss, ProductWorkspace throws ConfigurationError,
        // App.vue's onErrorCaptured swallowed it, and the page rendered only the
        // partial template up to the throw — visually a `<img alt="product-logo">`
        // placeholder with no surrounding chrome. (UX roadmap #17: the layout's
        // page <ErrorBoundary> now reports such an error and shows "This page
        // couldn't load." with a retry; ProductWorkspace wraps each tab in one.) The composables themselves
        // (useTrueSovereignProfits etc.) handle their own async data loading;
        // they don't require productsStore to be ready when the provider is
        // registered.
        app.provide< () => ProductDefinition >( cptProductKey, useCheckPointTrader );
        app.provide< () => ProductDefinition >( dmiProductKey, useDarkMoneyIndicator );
        app.provide< () => ProductDefinition >( dmtProductKey, useDeepMarketTrader );
        app.provide< () => ProductDefinition >( mttProductKey, useMonthlyTrendTrader );
        app.provide< () => ProductDefinition >( osProductKey, useOptionsSoup );
        app.provide< () => ProductDefinition >( psProductKey, useProfitSkimmer );
        app.provide< () => ProductDefinition >( sfProductKey, useSectorFocus );
        app.provide< () => ProductDefinition >( spProductKey, useSectorProphets );
        app.provide< () => ProductDefinition >( tamProductKey, useTechnicalAnalysisMillionaire );
        app.provide< () => ProductDefinition >( tspProductKey, useTrueSovereignProfits );
        app.provide< () => ProductDefinition >( sssProductKey, useShadowSpikeScanner );

        // Kick off products store loading in the background. App.vue also calls
        // appStore.initializeAsync (which awaits this same store), so the call
        // here is effectively a warm-up — initializeAsync is idempotent.
        useProductsStore( pinia ).initializeAsync();
    }
}

export default ProductContextsPlugin;
